import React, { useEffect, useState } from "react";
import {
  SidebarProvider,
  Sidebar,
  SidebarContent,
  SidebarInset,
} from "@/components/ui/sidebar";
import Workspace from "./Workspace";
import SidebarNav from "./SidebarNav";
import WorkspaceHeader from "./WorkspaceHeader";
import TutorialOverlay from "@/components/tutorial/TutorialOverlay";
import BetaSignupModal from "@/components/beta/BetaSignupSplash";
import { useUser } from "@clerk/clerk-react";
import { isDemoModeScoped } from "@/lib/demoMode";
import { useIsMobile } from "@/hooks/use-mobile";
import FeedbackDialog from "@/components/feedback/FeedbackDialog";

export default function AppShell() {
  const { user, isSignedIn, isLoaded } = useUser();
  const isMobile = useIsMobile();

  const [showTutorial, setShowTutorial] = useState(true);
  const [showBetaModal, setShowBetaModal] = useState(false);
  const [recentPrompts, setRecentPrompts] = useState<string[]>([]);

  useEffect(() => {
    const handler = (event: Event) => {
      const nextPrompt = (event as CustomEvent<{ prompt?: string }>).detail?.prompt?.trim();
      if (!nextPrompt) return;
      setRecentPrompts((prev) => [...prev.slice(-9), nextPrompt]);
    };
    window.addEventListener("chat:prompt-submitted", handler as EventListener);
    return () => window.removeEventListener("chat:prompt-submitted", handler as EventListener);
  }, []);

  if (!isLoaded) return null;

  const isCalceraDemoUser =
    isSignedIn &&
    isDemoModeScoped({
      userId: user?.id,
      email: user?.primaryEmailAddress?.emailAddress,
    });

  return (
    <SidebarProvider>
      {/* MOBILE BLOCKING MODAL */}
      {isMobile && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-[#0b0e1a]">
          <div className="mx-6 max-w-sm rounded-2xl border border-purple-500/30 bg-purple-950/80 px-8 py-10 text-center shadow-2xl backdrop-blur-xl">
            <div className="mb-5 flex justify-center">
              <img src="/calcera-logo.svg" alt="Calcera" className="h-10 w-auto" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
            </div>
            <h2 className="mb-3 text-lg font-semibold text-white">Mobile Version Coming Soon</h2>
            <p className="text-sm leading-relaxed text-purple-200/80">
              We are in the process of making a mobile version; view on desktop.
            </p>
          </div>
        </div>
      )}

      {/* TUTORIAL - DEMO ACCOUNT ONLY */}
      {isCalceraDemoUser && showTutorial && (
        <TutorialOverlay onFinish={() => setShowTutorial(false)} />
      )}

      {/* BETA SIGNUP MODAL (still disabled) */}
      {!isSignedIn && showBetaModal && (
        <BetaSignupModal onClose={() => setShowBetaModal(false)} />
      )}

      <div className="relative flex h-svh w-svw flex-col overflow-hidden bg-[#0b0e1a] text-white">
        <WorkspaceHeader />

        <div className="relative flex-1 min-h-0">
          <SidebarInset className="h-full w-full">
            <Workspace />
          </SidebarInset>

          <Sidebar
            className="
              absolute inset-y-0 left-0
              z-40
              backdrop-blur-xl bg-purple-950/60
              shadow-xl
            "
          >
            <div className="flex h-full min-h-0 flex-col">
              <SidebarContent className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden">
                <SidebarNav />
              </SidebarContent>
              {isSignedIn ? (
                <div className="flex shrink-0 justify-center px-4 pb-2 pt-2">
                  <FeedbackDialog recentPrompts={recentPrompts} />
                </div>
              ) : null}
            </div>
          </Sidebar>

          {isSignedIn ? <FeedbackDialog recentPrompts={recentPrompts} /> : null}
        </div>
      </div>
    </SidebarProvider>
  );
}
